/**
 * Section Parcours de l'onglet Import et paramètres — EF-NAV-001,
 * EF-GPX-001 à EF-GPX-004.
 *
 * Le résumé du parcours vit sous le bouton qui l'importe : c'est le seul
 * endroit où il est consulté, juste après une importation.
 */

import type { JSX } from 'preact';
import { chooseGpxFile, document_, readOnly } from '../state';
import { formatCount, formatDistance, formatElevation, formatPercent } from '../format';
import { ACTIONS, EMPTY_STATES } from '../labels';
import { Glyph } from '../icons';

export function RouteSection(): JSX.Element {
  const current = document_.value;

  const importButton = (
    <button
      type="button"
      class="primary wide"
      disabled={readOnly.value}
      onClick={() => {
        void chooseGpxFile();
      }}
    >
      <Glyph name="importer" label="" size={22} decorative />
      {ACTIONS.importGpx}
    </button>
  );

  if (current === null) {
    return (
      <>
        {importButton}
        <p class="muted">{EMPTY_STATES.noRoute.body}</p>
      </>
    );
  }

  const track = current.route.track;
  const element = current.route.selectedElement;

  return (
    <>
      {importButton}
      <dl class="definition-list">
          <div>
            <dt>Élément retenu</dt>
            <dd>
              {element.type === 'trk' ? 'Trace' : 'Route'} {element.index + 1}
              {element.name === null ? '' : ` — « ${element.name} »`}
            </dd>
          </div>
          <div>
            <dt>Fichier d’origine</dt>
            <dd>{current.route.sourceFileName ?? '—'}</dd>
          </div>
          <div>
            <dt>Longueur</dt>
            <dd>{formatDistance(track.lengthMeters)}</dd>
          </div>
          <div>
            <dt>D+</dt>
            <dd>{track.elevation.available ? formatElevation(track.elevationGainMeters) : 'indisponible'}</dd>
          </div>
          <div>
            <dt>Points du fichier</dt>
            <dd>{formatCount(current.route.originalPointCount)}</dd>
          </div>
          <div>
            <dt>Points de la trace de calcul</dt>
            <dd>{formatCount(track.count)} au pas de 100 m</dd>
          </div>
          <div>
            <dt>Couverture d’altitude</dt>
            <dd>
              {formatPercent(track.elevation.coveragePercent)} — trou maximal{' '}
              {formatDistance(track.elevation.maxGapMeters)}
            </dd>
          </div>
          <div>
            <dt>Altitudes interpolées / écartées</dt>
            <dd>
              {formatCount(track.elevation.interpolatedPointCount)} /{' '}
              {formatCount(track.elevation.filteredPointCount)}
            </dd>
          </div>
      </dl>

      {track.segmentGaps.length > 0 ? (
        <section class="card">
          <h3 style="margin-top:0;">Écarts entre segments</h3>
          <p class="muted">
            La distance est comptée dans le parcours et franchie à la vitesse sur le plat ; le D+
            correspondant n’est pas compté.
          </p>
          <ul>
            {track.segmentGaps.map((gap) => (
              <li key={gap.afterProgressionMeters}>
                {formatDistance(gap.distanceMeters)} après {formatDistance(gap.afterProgressionMeters)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {track.pointAnomalies.length > 0 ? (
        <section class="card">
          <h3 style="margin-top:0;">Écarts anormaux entre points consécutifs</h3>
          <p class="muted">
            Aucun point n’est écarté automatiquement : les parcours proviennent des organisations de course.
          </p>
          <ul>
            {track.pointAnomalies.map((anomaly) => (
              <li key={anomaly.afterProgressionMeters}>
                {formatDistance(anomaly.distanceMeters)} après {formatDistance(anomaly.afterProgressionMeters)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
